// @flow

import * as React from 'react'
import styled, { css } from 'styled-components'

import { type LayoutSetting, LAYOUT_OPTIONS } from './shared'

const WebshopGridSettingsLayoutButtons = ({
  onChange,
  value,
}: {
  onChange: (layout: LayoutSetting) => void,
  value: LayoutSetting,
}) => {
  return (
    <LayoutButtonsContainer>
      {LAYOUT_OPTIONS.map(option => {
        const active = value === option.value
        const iconColor = active ? '#000' : '#777'

        return (
          <LayoutButton
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            active={active}
          >
            <LayoutIcon iconColor={iconColor} layout={option.value} />
          </LayoutButton>
        )
      })}
    </LayoutButtonsContainer>
  )
}

export default WebshopGridSettingsLayoutButtons

const LayoutButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: 6px;
  justify-content: flex-start;
`

const LayoutButton = styled.button`
  width: 48px;
  height: 36px;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 3px;
  border: 1px dashed #ccc;
  background: #fff;

  outline: none;

  &:hover,
  &:focus-visible {
    border-color: #6c6c6c;
  }

  ${props =>
    props.active &&
    css`
      border-color: #000000;
    `}
`

const LayoutIcon = ({
  iconColor,
  layout,
}: {
  iconColor: string,
  layout: LayoutSetting,
}) => {
  return {
    '1x2': <LayoutIcon1x2 iconColor={iconColor} />,
    '1x3': <LayoutIcon1x3 iconColor={iconColor} />,
    '1x4': <LayoutIcon1x4 iconColor={iconColor} />,
    '2x2': <LayoutIcon2x2 iconColor={iconColor} />,
    '2+1': <LayoutIcon2plus1 iconColor={iconColor} />,
    '1+2': <LayoutIcon1plus2 iconColor={iconColor} />,
    masonry: <LayoutIconMasonry iconColor={iconColor} />,
    custom: <LayoutIconCustom iconColor={iconColor} />,
  }[layout]
}

const LayoutIcon1x2 = ({ iconColor }: { iconColor: string }) => (
  <svg
    width="30"
    height="18"
    viewBox="0 0 30 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="14" height="18" fill={iconColor} />
    <rect x="16" width="14" height="18" fill={iconColor} />
  </svg>
)

const LayoutIcon1x3 = ({ iconColor }: { iconColor: string }) => (
  <svg
    width="34"
    height="18"
    viewBox="0 0 34 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="10" height="18" fill={iconColor} />
    <rect x="12" width="10" height="18" fill={iconColor} />
    <rect x="24" width="10" height="18" fill={iconColor} />
  </svg>
)

const LayoutIcon1x4 = ({ iconColor }: { iconColor: string }) => (
  <svg
    width="34"
    height="18"
    viewBox="0 0 34 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="7" height="18" fill={iconColor} />
    <rect x="9" width="7" height="18" fill={iconColor} />
    <rect x="18" width="7" height="18" fill={iconColor} />
    <rect x="27" width="7" height="18" fill={iconColor} />
  </svg>
)

const LayoutIcon2x2 = ({ iconColor }: { iconColor: string }) => (
  <svg
    width="30"
    height="18"
    viewBox="0 0 30 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="14" height="8" fill={iconColor} />
    <rect y="10" width="14" height="8" fill={iconColor} />
    <rect x="16" width="14" height="8" fill={iconColor} />
    <rect x="16" y="10" width="14" height="8" fill={iconColor} />
  </svg>
)

const LayoutIcon2plus1 = ({ iconColor }: { iconColor: string }) => (
  <svg
    width="30"
    height="18"
    viewBox="0 0 30 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="14" height="8" fill={iconColor} />
    <rect y="10" width="14" height="8" fill={iconColor} />
    <rect x="16" width="14" height="18" fill={iconColor} />
  </svg>
)

const LayoutIcon1plus2 = ({ iconColor }: { iconColor: string }) => (
  <svg
    width="30"
    height="18"
    viewBox="0 0 30 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="14" height="18" fill={iconColor} />
    <rect x="16" width="14" height="8" fill={iconColor} />
    <rect x="16" y="10" width="14" height="8" fill={iconColor} />
  </svg>
)

const LayoutIconMasonry = ({ iconColor }: { iconColor: string }) => (
  <svg
    width="30"
    height="18"
    viewBox="0 0 30 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="19" height="8" fill={iconColor} />
    <rect x="11" y="10" width="19" height="8" fill={iconColor} />
    <rect y="10" width="9" height="8" fill={iconColor} />
    <rect x="21" width="9" height="8" fill={iconColor} />
  </svg>
)

const LayoutIconCustom = ({ iconColor }: { iconColor: string }) => (
  <LayoutIconCustomLabel iconColor={iconColor}>Custom</LayoutIconCustomLabel>
)

const LayoutIconCustomLabel = styled.div`
  margin: 2px 0 0 0;
  text-transform: uppercase;
  font-size: 9px;
  line-height: 1;
  font-weight: 500;
  color: ${props => props.iconColor};
`
